import React, { memo, useMemo, useState } from "react";
import { Form, Formik, Field } from "formik";
import {
  Box,
  Divider,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import { useOrder } from "../../hooks/useOrder";
import { useConfirm } from "material-ui-confirm";
import { handleConfirmation } from "../handleConfirmation";
import { SUPPORT_TICKET_ISSUE_TYPE } from "../constants";
import ButtonComponent from "../buttonComponent/ButtonComponent";
import { object, string, mixed } from "yup";
import { freshdeskApi } from "../../api";
import { useAuth } from "../../providers/AuthProvider/AuthProvider";
import { CreateSupportTicketRequest } from "../../api/freshdeskTypes";
import { useAccountInfo } from "../../hooks/useAccountInfo";
import { defaultFiveDaysAgo, today } from "../dates";

const initialValues = {
  category: "",
  description: "",
};

export const OrderSupportTicketCreationPage = memo(() => {
  const { t } = useTranslation();
  const { trackingCode } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { accountInfo } = useAccountInfo();

  const validationSchema = useMemo(
    () =>
      object({
        description: string()
          .trim()
          .required(t("errorsMessages.descriptionRequired"))
          .min(1, t("errorsMessages.descriptionRequired")),
        category: mixed()
          .oneOf(Object.values(SUPPORT_TICKET_ISSUE_TYPE))
          .required(t("errorsMessages.supportTicketCategoryRequired")),
      }),
    [t],
  );

  const onSubmit = async (values, formikBag) => {
    freshdeskApi
      .createSupportTicket(
        new CreateSupportTicketRequest({
          description: values.description,
          subject: `Ticket from ${accountInfo.contactName} (${trackingCode})`,
          name: accountInfo.contactName,
          email: accountInfo.email,
          status: 2,
          priority: 2,
          source: 7,
          type: values.category,
          parcelNumber: trackingCode,
        }),
      )
      .then(() => {
        enqueueSnackbar(t("alertsMessages.supportTicketCreated"), {
          variant: "success",
        });
        navigate(`/orders/${trackingCode}`);
      });
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({ isSubmitting, touched, values, errors, handleChange }) => (
          <>
            <Form className={`shadow-lg`}>
              <Typography variant={"h4"} className={"px-1 mx-4 mt-8 mb-6"}>
                {t("supportTicketCreation.title", { orderNumber: trackingCode })}
              </Typography>
              <Divider orientation={"horizontal"} flexItem />

              <Box className={"flex w-full flex-col p-4 gap-4 items-start"}>
                <FormControl
                  className="w-full md:w-3/5"
                  error={touched.category && Boolean(errors.category)}
                >
                  {/* TODO fix layout */}
                  <InputLabel id="category-label">
                    {t("supportTicketCreation.fields.issueType")}
                  </InputLabel>
                  <Select
                    labelId="category-label"
                    id="category"
                    name="category"
                    value={values.category}
                    onChange={handleChange}
                    label={t("supportTicketCreation.fields.issueType")}
                  >
                    {Array.from(Object.values(SUPPORT_TICKET_ISSUE_TYPE)).map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.category && errors.category && (
                    <FormHelperText>{errors.category}</FormHelperText>
                  )}
                </FormControl>

                <Field
                  as={TextField}
                  error={touched.description && Boolean(errors.description)}
                  label={`*${t("supportTicketCreation.fields.description")}`}
                  name="description"
                  placeholder={t("placeholders.description")}
                  helperText={(Boolean(touched.description) && errors.description) || undefined}
                  className={"w-3/5"}
                />

                <ButtonComponent
                  text={t("supportTicketCreation.actions.create")}
                  disabled={isSubmitting}
                  type="submit"
                  className={""}
                />
              </Box>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
});
